import React, { FC, useState } from 'react';
import Flex from 'ui/Flex/Flex';
import { Skeleton, Typography } from '@mui/material';
import { HttpError, RQAPICallWithData } from 'store/APIClient';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Selector from 'components/inputs/Selector';
import { translateBlock } from 'i18n';
import { capitalize } from 'helpers/helpers';
import { useTranslation } from 'react-i18next';
import ConfirmationModal from 'components/SharedComponents/Modals/ConfirmationModal';
import { SecondaryBudgetAxisSources } from 'routes/Support/components/ClientSupport/components/BudgetConfiguration/types';
import { createBudgetQueryKeys } from 'routes/BudgetModule/helpers/createBudgetQueryKeys';
import Loading from 'components/Loading';
import { useDispatch } from 'store/hooks';

const { secondaryBudgetAxisSourceKeys } = createBudgetQueryKeys();
const reactQueryKey = secondaryBudgetAxisSourceKeys();

/**
 * Get the value of the secondary axis source table.
 */
const useGetSecondaryBudgetAxisSourceTable = (bind: string) => {
    return useQuery<SecondaryBudgetAxisSources, HttpError>({
        queryKey: reactQueryKey,
        queryFn: async () => {
            const response = await RQAPICallWithData<{
                secondaryBudgetAxisSourceTable: SecondaryBudgetAxisSources;
            }>({
                endpoint: `support/${bind}/secondary_budget_axis_source_table`,
                method: 'GET',
            });
            return response.content.data?.secondaryBudgetAxisSourceTable;
        },
    });
};

/**
 * Update the value of the secondary axis source table.
 */
const useUpdateSecondaryBudgetAxisSourceTable = (bind: string) => {
    const queryClient = useQueryClient();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    return useMutation({
        mutationKey: reactQueryKey,
        mutationFn: (
            secondaryBudgetAxisSourceTable: SecondaryBudgetAxisSources,
        ) => {
            return RQAPICallWithData<{
                secondaryBudgetAxisSourceTable: SecondaryBudgetAxisSources;
            }>({
                endpoint: `support/${bind}/secondary_budget_axis_source_table`,
                method: 'PUT',
                body: { secondaryBudgetAxisSourceTable },
            });
        },
        onSuccess: (data) => {
            queryClient.setQueryData(
                reactQueryKey,
                data.content.data.secondaryBudgetAxisSourceTable,
            );
            dispatch.vision.notifyUser({
                message: t('support.snackbar.success'),
                snackType: 'success',
            });
        },
        onError: () => {
            dispatch.vision.notifyUser({
                message: t('support.snackbar.error'),
                snackType: 'error',
            });
        },
    });
};

/**
 * Get all the available source tables for the secondary analysis axis.
 * Dependant on active finance integrations.
 * @returns a list of source table names.
 */
const useGetAvailableSourceTables = (bind: string) => {
    return useQuery({
        queryKey: ['available_source_tables'],
        queryFn: async () => {
            const response = await RQAPICallWithData<{
                secondaryBudgetAxisSourceTables: SecondaryBudgetAxisSources[];
            }>({
                endpoint: `support/${bind}/secondary_budget_axis_source_table/available`,
                method: 'GET',
            });

            return response.content.data?.secondaryBudgetAxisSourceTables;
        },
    });
};

/**
 * Display all the configuration options for the budget module in Support.
 */
const BudgetConfiguration: FC<{ bind: string }> = ({ bind }) => {
    const { t } = useTranslation();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [pendingChange, setPendingChange] =
        useState<SecondaryBudgetAxisSources | null>(null);

    const {
        isError,
        isLoading,
        data: sourceTable,
    } = useGetSecondaryBudgetAxisSourceTable(bind);

    const { data: availableTables = [], isLoading: isAvailableLoading } =
        useGetAvailableSourceTables(bind);
    const { mutate } = useUpdateSecondaryBudgetAxisSourceTable(bind);

    if (isLoading || isAvailableLoading) {
        return <Loading />;
    }

    const onSourceTableChange = (value: SecondaryBudgetAxisSources) => {
        setIsModalOpen(true);
        setPendingChange(value);
    };

    const postValueOnConfirm = () => {
        if (!pendingChange) return;
        mutate(pendingChange);
        setIsModalOpen(false);
    };
    const cancelModification = () => {
        setIsModalOpen(false);
        setPendingChange(null);
    };
    const options = availableTables.map((table) => ({
        value: table,
        label: capitalize(translateBlock(table)),
    }));
    const sourceTableComponent =
        sourceTable || isError ? (
            <Selector
                label={t('support.budgetConfiguration.sourceTableLabel')}
                value={sourceTable}
                onChange={onSourceTableChange}
                options={options}
                error={isError}
                preventSorting
                variant="outlined"
                sx={{ flexShrink: 10, minWidth: 200 }}
            />
        ) : (
            <Skeleton
                variant="rectangular"
                sx={{
                    flexShrink: 10,
                    minWidth: 200,
                    height: '2.5rem',
                }}
            />
        );
    return (
        <>
            <Flex
                gap={2}
                margin={2}
                padding={2}
                direction="column"
                justify="space-between"
            >
                <Typography variant="h4">
                    {t('support.budgetConfiguration.title')}
                </Typography>
                <Flex direction="column" gap={2}>
                    <Flex alignItems="center" gap={4}>
                        <Typography sx={{ flexShrink: 1 }}>
                            {t('support.budgetConfiguration.sourceTable')}
                        </Typography>
                        {sourceTableComponent}
                    </Flex>
                </Flex>
            </Flex>
            <ConfirmationModal
                title={t('support.budgetConfiguration.confirmModalTitle')}
                isOpen={isModalOpen}
                onConfirm={postValueOnConfirm}
                onCancel={cancelModification}
            >
                <Typography sx={{ whiteSpace: 'pre-line' }}>
                    {t('support.budgetConfiguration.confirmation', {
                        previous: translateBlock(sourceTable).toUpperCase(),
                        next: translateBlock(pendingChange).toUpperCase(),
                    })}
                </Typography>
            </ConfirmationModal>
        </>
    );
};

export default BudgetConfiguration;
